import { AppButton, AppFilePicker, AppMultiInputText, AppTextField } from 'components';
import Editor from 'components/CKEditor/Editor';
import useManageTopicStory from 'hooks/API/useManageTopicStory';
import useToaster from 'hooks/useToaster';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Joi from 'joi';
import DOMPurify from 'dompurify';
import StoryCommentDropBox from '../../PreviewSection/StoryCommentDropBox';
import { AiOutlinePlusCircle } from 'react-icons/ai';

function ManageStories({ editMode, toggle, subtopicID, data = {} }) {
  const { topicID } = useParams();
  const [file, setFile] = useState(null);
  const [storyTitle, setStoryTitle] = useState('');
  const [storyDescription, setStoryDescription] = useState('');
  const [storyTags, setStoryTags] = useState([]);
  const [formErrors, setFormErrors] = useState([]);

  const [commentsModal, setCommentsModal] = useState(Object.assign({}, _initialStoryState));
  const [replyModal, setReplyModal] = useState({ open: false, apiHash: null });

  const { mutate, isLoading, isError } = useManageTopicStory({
    topicID,
    axiosConfigs: {
      params: { subtopicID },
    },
  });
  useToaster(isLoading, { type: isError ? 'error' : 'success' });

  function handleSave() {
    if (isLoading) return;

    setFormErrors([]);

    const data = {
      name: DOMPurify.sanitize(storyTitle),
      description: DOMPurify.sanitize(storyDescription),
      storyTags: storyTags?.map((x) => x.value),
      imageUrl: file?.meta?.file,
    };

    const validation = manageStorySchema.validate(data, {
      abortEarly: false,
      allowUnknown: true,
    });

    if (validation?.error) {
      setFormErrors(validation.error?.details ?? []);
      return;
    }

    mutate({ data, type: 'CREATE' });
    clear();
  }

  function updateStory(args = {}) {
    if (isLoading) return;

    const data = {
      name: DOMPurify.sanitize(storyTitle),
      description: DOMPurify.sanitize(storyDescription),
      storyTags: storyTags?.map((x) => x.value),
    };

    mutate({ data: { ...data, ...args }, type: 'UPDATE' });
    clear();
  }

  useEffect(() => {
    if (editMode?.storyTags) {
      setStoryTags(editMode?.storyTags?.map?.((x) => ({ label: x, value: x })));
    }
    if (editMode?.name) {
      setStoryTitle(editMode?.name);
    }
    if (editMode?.description) {
      setStoryDescription(DOMPurify.sanitize(editMode?.description));
    }
  }, [editMode]);

  function clear() {
    setStoryTitle('');
    setStoryDescription('');
    setStoryTags([]);

    toggle();
  }

  const selectedCellNo = data?.story?.findIndex((x) => x._id === editMode?._id);
  const reviewCellNo = data?.story?.findIndex((x) => x._id === replyModal.apiHash);

  const cellNo = reviewCellNo > -1 ? reviewCellNo : selectedCellNo;

  return (
    <>
      {(replyModal.open || commentsModal.open) && (
        <StoryCommentDropBox
          open={replyModal.open || commentsModal.open}
          close={() => {
            setCommentsModal(Object.assign({}, _initialStoryState));
            setReplyModal({ open: false, apiHash: null });
          }}
          data={{
            replyModal,
            cellNo,
            apiBodyProps: {
              topic: editMode?.topic,
              subtopic: subtopicID,
              chapter: editMode?.chapter,
              class: editMode?.class,
              story: editMode?._id,
            },
          }}
        />
      )}
      <div className="row">
        <div className="col-12">
          {editMode?.id && (
            <h2 className="prime-head p-0 boldtitle">
              File selected: {editMode?.name || 'Untitled story'}
            </h2>
          )}
          {!editMode?.id && <h2 className="prime-head p-0 boldtitle">Add story</h2>}
          <span className="boldtitle mt-5 conaddtitle">Add details</span>
        </div>
      </div>

      <div className="row">
        <div className="col-lg-5 col-md-6 col-sm-12">
          <div className="w-100 mt-4">
            <AppTextField
              inputProps={{
                value: storyTitle,
                onChange: (event) => setStoryTitle(event.target.value),
                placeholder: 'Story Title',
              }}
              labelProps={{ label: 'Story Title' }}
            />
          </div>
          <div className="w-100 mt-4">
            <label htmlFor="">Story Description</label>
            <Editor
              data={{
                value: storyDescription,
                onChange: (data) => setStoryDescription(data ?? ''),
              }}
            />
          </div>
          <div className="w-100 mt-4 ">
            <AppMultiInputText
              heading={'Story Search Tags'}
              value={storyTags}
              setValue={(data) => setStoryTags(data)}
            />
          </div>
        </div>

        {!editMode?._id && (
          <div className="col-lg-5 col-md-6  col-sm-12  ">
            <div className="w-100 mt-4">
              <label htmlFor="">
                Choose Story Pages from this Local Machine (JPEG, PNG Format)
              </label>
              <div>
                <AppFilePicker
                  text="Upload PNG"
                  handleChange={(data) => setFile(data)}
                  inputProps={{ accept: 'image/*' }}
                />
              </div>
            </div>
          </div>
        )}

        {editMode?._id && (
          <>
            <div className="col-lg-5 col-md-6 col-sm-12">
              <div className="w-100 mt-4">
                <AppButton
                  variantClass="btn-plane d-flex align-items-center"
                  onClick={() => {
                    setCommentsModal({ open: true, data: {} });
                  }}
                >
                  <AiOutlinePlusCircle className="mx-2" />
                  Add Comment
                </AppButton>

                {editMode?.storyReviews?.length > 0 && (
                  <div
                    className="mt-4"
                    style={{ maxHeight: '200px', height: '170px', overflow: 'scroll' }}
                  >
                    <p>
                      <strong>Comments</strong>
                    </p>
                    <table className="table">
                      <thead>
                        <tr>
                          <th scope="col">#</th>
                          <th scope="col">Comment</th>
                        </tr>
                      </thead>
                      <tbody>
                        {editMode?.storyReviews?.map((review, index) => {
                          return (
                            <tr key={review?._id}>
                              <th scope="row">{index + 1}</th>
                              <td
                                onClick={() => {
                                  if (review?.resolved) return;

                                  setReplyModal({ open: true, apiHash: review?._id });
                                }}
                                className="displayPointer"
                                dangerouslySetInnerHTML={{
                                  __html: DOMPurify.sanitize(review?.reviewComment),
                                }}
                              ></td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      <div className="row mt-5 btnsubmitmain">
        <div className="col-12 btnTab-foot">
          <button
            type="button"
            className="btn-right-content save"
            onClick={() => {
              if (editMode?._id) {
                updateStory({ id: editMode?._id });
                return;
              }

              handleSave();
            }}
          >
            Save Changes
          </button>
          {editMode?._id && (
            <AppButton variantClass="btn-plane p--tint mx-4" onClick={clear}>
              Clear
            </AppButton>
          )}
        </div>
      </div>
    </>
  );
}

const manageStorySchema = Joi.object({
  imageUrl: Joi.string().required().trim(),
});

const _initialStoryState = {
  open: false,
  data: {},
};

export default ManageStories;
